/**
 * Checklist Widget
 *
 * Multi-select checklist used for journey purpose selection and similar flows.
 */

import { cn } from '@tonnie/foundation';
import { useTranslation } from '@tonnie/locale';
import { Button, Card, CardContent } from '@tonnie/ui';
import {
  Briefcase,
  Building2,
  CheckCircle,
  CheckCircle2,
  ClipboardCheck,
  Dog,
  FileText,
  Globe,
  GraduationCap,
  Heart,
  Home,
  Hotel,
  Monitor,
  Plane,
  Scale,
  Shield,
  Sparkles,
  Users,
} from '@tonnie/assets';
import { useCallback, useMemo, useState } from 'react';
import type { WidgetProps } from '@tonnie/copilot';

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

interface IChecklistOption {
  id: string;
  title: string;
  description?: string;
  iconName?: string;
  value: string;
}

interface IChecklistConfig {
  title?: string;
  description?: string;
  options?: IChecklistOption[];
  minSelections?: number;
  maxSelections?: number;
  allowMultiple?: boolean;
}

// Icon mapping by iconName
const ICON_MAP: Record<string, React.ReactNode> = {
  Building2: <Building2 className="w-6 h-6" />,
  Briefcase: <Briefcase className="w-6 h-6" />,
  CheckCircle: <CheckCircle className="w-6 h-6" />,
  ClipboardCheck: <ClipboardCheck className="w-6 h-6" />,
  Dog: <Dog className="w-6 h-6" />,
  FileText: <FileText className="w-6 h-6" />,
  Globe: <Globe className="w-6 h-6" />,
  GraduationCap: <GraduationCap className="w-6 h-6" />,
  Heart: <Heart className="w-6 h-6" />,
  Home: <Home className="w-6 h-6" />,
  Hotel: <Hotel className="w-6 h-6" />,
  Monitor: <Monitor className="w-6 h-6" />,
  Plane: <Plane className="w-6 h-6" />,
  Scale: <Scale className="w-6 h-6" />,
  Shield: <Shield className="w-6 h-6" />,
  Sparkles: <Sparkles className="w-6 h-6" />,
  Users: <Users className="w-6 h-6" />,
};

// Fallback icon mapping by option ID (used when iconName is missing from DB config)
const ICON_FALLBACK_BY_ID: Record<string, React.ReactNode> = {
  checkup: <ClipboardCheck className="w-6 h-6" />,
  job: <Briefcase className="w-6 h-6" />,
  relocation: <Plane className="w-6 h-6" />,
  culture: <Globe className="w-6 h-6" />,
  care: <Heart className="w-6 h-6" />,
};

// ═══════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════

/**
 * ChecklistWidget - Multi-select checklist for journey purpose selection and similar flows.
 *
 * Renders a list of toggleable options from `config.options`. Supports min/max selection
 * constraints and single/multi-select modes. The `onSelect` callback is only fired when
 * the user explicitly clicks the confirm button — never on individual toggles.
 *
 * @param config - Widget configuration (options, minSelections, maxSelections, allowMultiple, title, description)
 * @param onSelect - Callback fired with an array of selected option values on confirm
 * @returns React component for multi-select checklist
 */
export function ChecklistWidget({
  config,
  onSelect,
}: WidgetProps & { config?: IChecklistConfig; onSelect?: (values: string[]) => void }) {
  const { t } = useTranslation();
  const options = useMemo(() => config?.options ?? [], [config?.options]);
  const allowMultiple = config?.allowMultiple !== false;
  const minSelections = config?.minSelections ?? 0;
  const maxSelections = config?.maxSelections ?? (allowMultiple ? options.length : 1);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const optionsWithIcons = useMemo(
    () =>
      options.map((opt) => ({
        ...opt,
        icon: (opt.iconName ? ICON_MAP[opt.iconName] : undefined) ?? ICON_FALLBACK_BY_ID[opt.id],
      })),
    [options]
  );

  const handleToggle = useCallback(
    (optionId: string) => {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(optionId)) {
          next.delete(optionId);
        } else {
          if (!allowMultiple) next.clear();
          if (next.size < maxSelections) next.add(optionId);
        }
        return next;
      });
    },
    [allowMultiple, maxSelections]
  );

  const canConfirm = selectedIds.size >= minSelections;

  return (
    <div className="w-full space-y-3 py-2" data-testid="widget-checklist">
      {config?.title && <h3 className="text-sm font-semibold">{config.title}</h3>}
      {config?.description && <p className="text-xs text-muted-foreground">{config.description}</p>}
      <div className="space-y-2">
        {optionsWithIcons.map((option) => {
          const isSelected = selectedIds.has(option.id);
          return (
            <Card
              key={option.id}
              data-testid={`widget-checklist-item-${option.id}`}
              role="checkbox"
              tabIndex={0}
              aria-checked={isSelected}
              aria-label={option.title}
              onClick={() => handleToggle(option.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleToggle(option.id);
                }
              }}
              className={cn(
                'cursor-pointer border-2 transition-all hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
                isSelected
                  ? 'border-primary bg-primary/5 shadow-sm'
                  : 'border-border hover:border-primary/30'
              )}
            >
              <CardContent className="p-3 flex items-center gap-3">
                {/* Checkbox */}
                <div
                  className={cn(
                    'w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors',
                    isSelected
                      ? 'bg-primary border-primary text-primary-foreground'
                      : 'border-muted-foreground/40'
                  )}
                >
                  {isSelected && <CheckCircle2 className="w-3.5 h-3.5" />}
                </div>

                {/* Icon */}
                {option.icon && (
                  <div
                    className={cn(
                      'p-2 rounded-lg shrink-0',
                      isSelected
                        ? 'bg-primary/10 text-primary-accessible'
                        : 'bg-muted text-muted-foreground'
                    )}
                  >
                    {option.icon}
                  </div>
                )}

                {/* Text */}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">{option.title}</p>
                  {option.description && (
                    <p className="text-xs text-muted-foreground line-clamp-1">
                      {option.description}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Confirm button */}
      {selectedIds.size > 0 && (
        <div className="flex justify-end pt-1">
          <Button
            size="sm"
            disabled={!canConfirm}
            onClick={() => {
              const values = options.filter((o) => selectedIds.has(o.id)).map((o) => o.value);
              onSelect?.(values);
            }}
            data-testid="widget-checklist-confirm"
          >
            <CheckCircle2 className="w-4 h-4 mr-1" />
            {t('common.confirm')} ({selectedIds.size})
          </Button>
        </div>
      )}
    </div>
  );
}
